import { get, isFunction, omitBy, isNil, isEqual, getOr, pick } from 'lodash/fp';
import { toJS } from 'mobx';

// share dependencies
import AlignmentSetting from 'share/components/controls/Izenda.AlignmentSetting';
import BorderSettings from 'share/components/controls/Izenda.BorderSettings';
import ColorStyling from 'share/components/controls/Izenda.ColorStyling';
import FontFormat from 'share/components/controls/Izenda.FontFormat';
import FontStyle from 'share/components/controls/Izenda.FontStyle';
import Panel from 'share/components/controls/Izenda.Panel';
import CustomInput from 'share/components/controls/Izenda.CustomInput';
import localizer from 'share/localization/Izenda.Localizer';
import { metricProperties,CUSTOM_URL } from 'logic/data/Izenda.ReportPartKPIConstants';

// local dependencies
import TileBorderPropertyPreview from './Izenda.TileBorderPropertyPreview';

const SCHEMA = {
    schemaKey: 'KPI_TILE_PROPERTIES',
    fields: {
        border: {
            component: 'Panel',
            props: {
                initState: true,
                collapsible: true,
                header: {
                    title: 'Border',
                    icon: 'icon-toggle-triangle'
                }
            },
            fields: {
                border: {
                    title: 'Tile Border',
                    type: 'btnPopup',
                    isHidden: 'isHiddenForImageTiles',
                    fnValue: 'getValueByKey',
                    props: {
                        fnResetDefaultValue: 'resetBorders',
                        hadSetting: 'hasBorderSetting',
                        previewComponent: TileBorderPropertyPreview,
                        disableMultipleBorderSettings: true,
                        previewData: 'getTileBorderProps'
                    },
                    staticProps: {
                        popupTitle: 'Tile Border',
                        className: 'icon-setting btn-icon',
                        popupContent: BorderSettings,
                        popupProps: {
                            type: 'none'
                        }
                    }
                },
            }
        },
        format: {
            component: 'Panel',
            props: {
                initState: true,
                collapsible: true,
                header: {
                    title: localizer.getText('COMMON_LABEL_FORMAT'),
                    icon: 'icon-toggle-triangle'
                }
            },
            fields: {
                fontFormat: {
                    title: localizer.getText('FONT_SETTINGS_LABEL_FONT'),
                    component: 'FontFormat',
                    fnValue: 'getValueByKey',
                    isHidden: 'isHiddenForImageTiles',
                    props: {
                        hiddenByState: 'hiddenByTileType',
                        isFontSizeHidden: 'isHiddenForImageAndMetricTiles'
                    }
                },
                fontStyle: {
                    title: localizer.getText('COMMON_LABEL_STYLE'),
                    component: 'FontStyle',
                    isHidden: 'isHiddenForImageTiles',
                    fnValue: 'getValueByKey'
                },
                color: {
                    title: localizer.getText('COMMON_LABEL_COLOR'),
                    component: 'ColorStyling',
                    fnValue: 'getValueByKey',
                    isHidden: 'isHiddenForImageTiles',
                    props: {
                        type: 'photoshop'
                    }
                },
                alignment: {
                    title: localizer.getText('FONT_SETTINGS_LABEL_ALIGNMENT'),
                    component: 'AlignmentSetting',
                    fnValue: 'getValueByKey',
                    isHidden: 'isHiddenForImageAndMetricTiles'
                },
                image: {
                    title: localizer.getText('FORMAT_PROPERTIES_LABEL_IMAGE_URL'),
                    component: 'CustomInput',
                    fnValue: 'getValueByKey',
                    isHidden: 'isHiddenForTextOrMetricTiles'
                }

            }
        },
        dataFormattings: {
            component: 'Panel',
            props: {
                initState: true,
                collapsible: true,
                header: {
                    title: localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_DATA_FORMATTING_HEADER'),
                    icon: 'icon-toggle-triangle'
                }
            },
            fields: {
                // custom URL: button to open pop-up
                customURL: {
                    get title() {
                        return localizer.getText('COMMON_LABEL_CUSTOM_URL');
                    },
                    type: 'btnPopup',
                    fnValue: 'getCustomUrl',
                    props: {
                        checkUniqLinkOption: 'checkUniqLinkOption',
                        hadSetting: 'hasSettingCustomURL',
                        loadValue: 'getCustomUrl',
                        fnResetDefaultValue: 'resetDefaultCustomURL'
                    },
                    isHidden: 'isHiddenCustomURL',
                    staticProps: {
                        get title() {
                            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_CUSTOM_URL_SETTINGS');
                        },
                        className: 'icon-setting btn-icon',
                        get popupTitle() {
                            return localizer.getText('REPORT_PART_FIELD_PROP_SCHEMA_CUSTOM_URL_SETTINGS');
                        },
                        popupContent: require('modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.CustomURLBody'),
                        popupProps: {
                            type: 'none',
                            autoClose: false
                        },
                        popupContentProps: {
                            beforeClosing: 'beforeClosing'
                        }
                    }
                }
            }
        }
    }
};

const WIDGETS = {
    AlignmentSetting,
    BorderSettings,
    ColorStyling,
    FontFormat,
    FontStyle,
    CustomInput,
    Panel
};

export default function (reportDetails) {
    const activeTile = reportDetails.reportPartContent.activeTile;
    const getProperties = (key) => {
        return activeTile ? activeTile.getPropertiesByKey(key) : {};
    }

    return {
        definedSchema: SCHEMA,
        widgets: WIDGETS,
        source: {},
        // function for getting prop values for each component in the schema
        mappingProps: {
            hasBorderSetting() {
                const currentSetting = getProperties('border');
                return !isEqual(currentSetting, metricProperties.border);
            },
            resetBorders() {
                return metricProperties.border;
            },
            resetDefaultCustomURL() {
                return { option: CUSTOM_URL.DEFAULT_VALUE, url: '' };
            },
            hasSettingCustomURL() {
                return activeTile && !!activeTile.properties.customURL.url;
            }
        },
        // functions for getting calculated values in schema
        mappingSource: {
            getTileBorderProps() {
                return {
                    tile: activeTile,
                    reportPart: reportDetails
                };
            },
            isHiddenForImageTiles() {
                return !activeTile || activeTile.isImageTile;
            },
            isHiddenForTextOrMetricTiles() {
                return !activeTile || !activeTile.isImageTile;
            },
            isHiddenForImageAndMetricTiles() {
                return !activeTile || activeTile.isImageOrMetricTile;
            },
            checkUniqLinkOption() {
                const properties = activeTile;
                return activeTile && activeTile.checkUniqLinkOption.bind(properties);
            },
            getCustomUrl() {
                const customURL = getOr({}, 'properties.customURL', activeTile);
                return pick(['url', 'option', 'selectedIconValue', 'viewSettingByLink'], customURL);
            },

           
            getValueByKey: getProperties
        },
        onChangeValue: function (
            schemaData,
            changedKey,
            changedKeyPath,
            changedValue,
            changedOthersInfo,
            derivedChange
        ) {

            var groupData = changedKeyPath && changedKeyPath.length ? get(changedKeyPath, schemaData) : schemaData;
            var fieldData = groupData[changedKey];

            fieldData && (fieldData.value = changedValue);
            isFunction(activeTile.properties.updateProperties) && activeTile.properties.updateProperties({
                [changedKey]: changedValue
            });


            const chartProperties = {
                optionByType: {
                    metricTile: reportDetails.reportPartContent.getMetricTileList()
                }
            };

            // remove properties which don't have for selected chart-type
            chartProperties.optionByType = omitBy(isNil, chartProperties.optionByType);

            derivedChange && derivedChange(chartProperties);
            reportDetails.reportPartContent.setReportPartProperties(chartProperties);

            return Promise.resolve({
                needToUpdate: false,
                chartProperties
            });
        }
    }
}






// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartKPI/Izenda.KPITilePropertiesSchema.js